export type Language = 'en' | 'fr'

export const translations = {
  en: {
    nav: {
      home: 'Home',
      about: 'About',
      services: 'Services',
      pricing: 'Pricing',
      clinic: 'FGM Hope Clinic',
      contact: 'Contact',
      testimonials: 'Testimonials',
      recommendations: 'Recommendations',
      bookNow: 'Book Now',
    },
    hero: {
      headline: 'Compassionate Midwifery Care in Auckland and Online',
      subheadline:
        'Personalized support for pregnancy, birth preparation, and lactation — with gentle, expert care in English and French.',
      ctaBook: 'Book a consultation',
      ctaServices: 'View services',
      badges: {
        location: 'Based in Auckland',
        virtual: 'Virtual consultations available',
        languages: 'English & French',
        trauma: 'Trauma-informed care',
      },
    },
    about: {
      title: 'About Aissa',
      bioParagraphs: [
        "Aïssa Sara Edon is a compassionate French-British midwife based in Auckland, offering personalized, culturally sensitive care to women and families both locally and through virtual consultations worldwide. Her work combines professional expertise, warmth, and deep respect for each woman's unique journey.",
        "Born in Africa and a survivor of female genital mutilation (FGM), Aïssa is also a dedicated activist and advocate for women's health. She speaks publicly about her experience to raise awareness, break the silence, and help drive lasting change for survivors and communities around the world.",
        'With over 16 years of experience supporting families across Europe, Africa, and the Middle East, she specializes in trauma-informed, compassionate maternity care — particularly for first-time mothers, survivors of trauma, and women navigating pregnancy and parenthood far from home.',
        "Aïssa is the founder of The FGM Hope Clinic, an organization supporting survivors of FGM, and has worked extensively with Médecins Sans Frontières (MSF), the NHS, and other international healthcare systems. In recognition of her work, she was named one of the BBC's 100 Women in 2015 and received the Mary Seacole Leadership Scholar Award from the Royal College of Nursing.",
        "Through consultations in English and French, Aïssa offers expert support in pregnancy, birth preparation, lactation, postnatal care, and women's health advocacy. She combines clinical excellence with deep emotional support, because she understands how important it is for every woman to feel truly seen, heard, and empowered.",
      ],
      whyWorkTitle: 'Why work with Aissa?',
      points: [
        'Gentle and personalized support',
        'English and French consultations',
        'Auckland-based with online sessions available',
        'Professional, human, culturally sensitive care',
      ],
    },
    services: {
      title: 'Services',
      subtitle: 'Gentle, expert care tailored to your unique journey.',
      lactation: {
        title: 'Lactation Consultations',
        desc: 'One-on-one support to help mothers feel confident, informed, and supported with breastfeeding and feeding challenges.',
        cta: 'Learn more',
      },
      birthPrep: {
        title: 'Empowered Birth Preparation',
        desc: 'Personalized birth preparation sessions designed to help women feel informed, calm, and empowered before labour and birth.',
        cta: 'Learn more',
      },
      breastfeeding: {
        title: 'Breastfeeding Support',
        desc: 'Ongoing breastfeeding support for mothers who need guidance, reassurance, and practical help.',
        status: 'Coming soon – September',
        cta: 'Learn more',
      },
    },
    pricing: {
      title: 'Pricing',
      subtitle: 'Transparent pricing for personalized care.',
      note: 'Prices are listed in New Zealand Dollars, with approximate US Dollar equivalents for international clients.',
      cards: {
        lactation: {
          title: 'Lactation Consultation',
          nzd: 'NZD $150',
          usd: 'Approx. USD $90',
        },
        birthSingle: {
          title: 'Empowered Birth Preparation',
          subtitle: 'Single Session',
          nzd: 'NZD $150',
          usd: 'Approx. USD $90',
        },
        birthPackage: {
          title: 'Empowered Birth Preparation',
          subtitle: '7 Session Package',
          nzd: 'NZD $900',
          usd: 'Approx. USD $540',
        },
        breastfeeding: {
          title: 'Breastfeeding Support',
          status: 'Coming Soon – September',
        },
      },
    },
    fgm: {
      title: 'FGM Hope Clinic',
      text: "FGM Hope Clinic is an important part of Aissa Edon's mission to support, educate, and advocate for women affected by FGM. We provide a safe, respectful, and compassionate environment for care and healing.",
      button: 'Learn more',
    },
    booking: {
      title: 'Ready to feel supported?',
      text: 'Book a consultation with Aissa and receive gentle, professional care tailored to your needs — in Auckland or online.',
      btnBook: 'Book now',
      btnContact: 'Contact Aissa',
    },
    contact: {
      title: 'Get in Touch',
      subtitle: 'Reach out to schedule a consultation or ask any questions.',
      form: {
        name: 'Your Name',
        email: 'Email Address',
        message: 'How can I help you?',
        submit: 'Send Message',
      },
      info: {
        location: 'Auckland, New Zealand',
        virtual: 'Virtual consultations available worldwide',
        email: 'hello@aissaedon.com',
      },
    },
    footer: {
      name: 'Aissa Edon',
      role: 'Midwifery Care | Auckland & Online',
      rights: 'All rights reserved.',
    },
    testimonials: {
      title: 'Client Testimonials',
      subtitle: 'Hear from families I have had the honor to support.',
      list: [
        {
          quote:
            'Aïssa is an exceptional midwife with deep expertise and a warm, reassuring approach. She gave me highly tailored advice for delivery in the Saudi context and can adapt seamlessly to different cultural and medical settings. Her responsiveness makes her a trusted partner for anyone seeking evidence-based care every step of the way.',
          name: 'S&C',
          context: 'Birth preparation & postnatal care',
          date: 'October 2023',
        },
        {
          quote:
            'Thank you so much for coming back, helping us through the journey of our second pregnancy, for being so understanding and being the little hope and light we needed. You made my dream come true, never in a million times I was thinking of that outcome. Hope we stay in touch.',
          name: 'Emily R.',
          context: 'Birth preparation & postnatal care',
          date: 'January 2024',
        },
        {
          quote:
            "Having consultations in French while living in New Zealand was incredibly comforting. Aissa's expertise and warmth made our transition to parenthood so much smoother.",
          name: 'Camille T.',
          context: 'Online pregnancy support',
          date: 'November 2023',
        },
        {
          quote:
            "Aissa's deep understanding of cultural sensitivities and her compassionate nature truly set her apart. She empowered me to advocate for myself during birth.",
          name: 'Amina K.',
          context: 'Empowered birth preparation',
          date: 'March 2024',
        },
        {
          quote:
            'I reached out to Aissa for postnatal support when I was feeling completely overwhelmed. Her guidance was practical, reassuring, and exactly what I needed.',
          name: 'Jessica L.',
          context: 'Postnatal support',
          date: 'February 2024',
        },
      ],
    },
    recommendations: {
      title: 'Recommended Professionals',
      subtitle:
        'Trusted colleagues and specialists who complement midwifery care.',
      list: [
        {
          name: 'Juliette Cazabat',
          specialty: 'Psychotherapist & Relationship Counsellor',
          description:
            'Specializing in maternal mental health, postpartum depression, birth trauma recovery and family support. She works from an holistic perspective.',
          contact: 'juliette-cazabat.com',
        },
        {
          name: 'Alex',
          specialty: 'Maternity Photography',
          description:
            'Capturing the beautiful journey of motherhood and providing precious memories with expert photography sessions.',
          contact: 'portfolio',
          buttonLabel: 'Portfolio',
        },
        {
          name: 'Forever Living Products',
          specialty: 'Wellness Products',
          description:
            'A game of natural products made with pure Aloe vera, supporting health, wellness, and self-care during the maternity journey.',
          contact: 'foreverliving.com/gbr/en-gb/home',
          buttonLabel: 'Shop Products',
        },
      ],
    },
  },
  fr: {
    nav: {
      home: 'Accueil',
      about: 'À propos',
      services: 'Services',
      pricing: 'Tarifs',
      clinic: 'FGM Hope Clinic',
      contact: 'Contact',
      testimonials: 'Témoignages',
      recommendations: 'Recommandations',
      bookNow: 'Réserver',
    },
    hero: {
      headline:
        'Accompagnement bienveillant par une sage-femme à Auckland et en ligne',
      subheadline:
        "Un accompagnement personnalisé pour la grossesse, la préparation à la naissance et l'allaitement — avec une approche douce, experte et disponible en français et en anglais.",
      ctaBook: 'Prendre rendez-vous',
      ctaServices: 'Voir les services',
      badges: {
        location: 'Basée à Auckland',
        virtual: 'Consultations virtuelles disponibles',
        languages: 'Anglais & Français',
        trauma: 'Soins tenant compte des traumatismes',
      },
    },
    about: {
      title: "À propos d'Aissa",
      bioParagraphs: [
        'Aïssa Sara Edon est une sage-femme franco-britannique bienveillante basée à Auckland, offrant des soins personnalisés et culturellement adaptés aux femmes et aux familles, aussi bien localement que par le biais de consultations virtuelles dans le monde entier. Son travail allie expertise professionnelle, chaleur humaine et un profond respect pour le parcours unique de chaque femme.',
        'Née en Afrique et survivante de mutilations génitales féminines (MGF), Aïssa est également une militante et défenseuse engagée de la santé des femmes. Elle témoigne publiquement de son expérience pour sensibiliser, briser le silence et contribuer à un changement durable pour les survivantes et les communautés du monde entier.',
        "Forte de plus de 16 ans d'expérience auprès de familles en Europe, en Afrique et au Moyen-Orient, elle est spécialisée dans les soins de maternité bienveillants et tenant compte des traumatismes — en particulier pour les femmes enceintes pour la première fois, les survivantes de traumatismes et les femmes vivant leur grossesse et leur maternité loin de chez elles.",
        "Aïssa est la fondatrice de The FGM Hope Clinic, une organisation de soutien aux survivantes de MGF, et a travaillé de manière approfondie avec Médecins Sans Frontières (MSF), le NHS et d'autres systèmes de santé internationaux. En reconnaissance de son travail, elle a été nommée parmi les 100 Women de la BBC en 2015 et a reçu le Mary Seacole Leadership Scholar Award du Royal College of Nursing.",
        'À travers des consultations en anglais et en français, Aïssa offre un accompagnement expert en grossesse, préparation à la naissance, allaitement, soins postnataux et plaidoyer pour la santé des femmes. Elle allie excellence clinique et soutien émotionnel profond, car elle comprend combien il est important pour chaque femme de se sentir véritablement vue, entendue et autonome.',
      ],
      whyWorkTitle: 'Pourquoi choisir Aissa ?',
      points: [
        'Accompagnement doux et personnalisé',
        'Consultations en anglais et en français',
        'Basée à Auckland avec sessions en ligne disponibles',
        'Soins professionnels, humains et respectueux',
      ],
    },
    services: {
      title: 'Services',
      subtitle: 'Des soins doux et experts adaptés à votre parcours unique.',
      lactation: {
        title: 'Consultations en lactation',
        desc: "Un accompagnement individuel pour aider les mères à se sentir confiantes, informées et soutenues face aux défis de l'allaitement.",
        cta: 'En savoir plus',
      },
      birthPrep: {
        title: 'Préparation à la naissance',
        desc: "Des séances de préparation à la naissance personnalisées conçues pour aider les femmes à se sentir informées, calmes et autonomes avant le travail et l'accouchement.",
        cta: 'En savoir plus',
      },
      breastfeeding: {
        title: "Soutien à l'allaitement",
        desc: "Un soutien continu à l'allaitement pour les mères qui ont besoin de conseils, de réassurance et d'aide pratique.",
        status: 'Bientôt disponible – Septembre',
        cta: 'En savoir plus',
      },
    },
    pricing: {
      title: 'Tarifs',
      subtitle: 'Des tarifs transparents pour des soins personnalisés.',
      note: 'Les prix sont indiqués en dollars néo-zélandais, avec des équivalents approximatifs en dollars américains pour les clients internationaux.',
      cards: {
        lactation: {
          title: 'Consultation en lactation',
          nzd: 'NZD $150',
          usd: 'Env. USD $90',
        },
        birthSingle: {
          title: 'Préparation à la naissance',
          subtitle: 'Séance unique',
          nzd: 'NZD $150',
          usd: 'Env. USD $90',
        },
        birthPackage: {
          title: 'Préparation à la naissance',
          subtitle: 'Forfait 7 séances',
          nzd: 'NZD $900',
          usd: 'Env. USD $540',
        },
        breastfeeding: {
          title: "Soutien à l'allaitement",
          status: 'Bientôt disponible – Septembre',
        },
      },
    },
    fgm: {
      title: 'FGM Hope Clinic',
      text: "La clinique FGM Hope est une part importante de la mission d'Aissa Edon pour soutenir, éduquer et défendre les femmes touchées par les MGF. Nous offrons un environnement sûr, respectueux et bienveillant pour les soins et la guérison.",
      button: 'En savoir plus',
    },
    booking: {
      title: 'Prête à vous sentir soutenue ?',
      text: 'Prenez rendez-vous avec Aissa et recevez des soins doux et professionnels adaptés à vos besoins — à Auckland ou en ligne.',
      btnBook: 'Réserver maintenant',
      btnContact: 'Contacter Aissa',
    },
    contact: {
      title: 'Contact',
      subtitle:
        "N'hésitez pas à nous contacter pour planifier une consultation ou poser vos questions.",
      form: {
        name: 'Votre Nom',
        email: 'Adresse Email',
        message: 'Comment puis-je vous aider ?',
        submit: 'Envoyer le message',
      },
      info: {
        location: 'Auckland, Nouvelle-Zélande',
        virtual: 'Consultations virtuelles disponibles dans le monde entier',
        email: 'hello@aissaedon.com',
      },
    },
    footer: {
      name: 'Aissa Edon',
      role: 'Soins de sage-femme | Auckland & en ligne',
      rights: 'Tous droits réservés.',
    },
    testimonials: {
      title: 'Témoignages de clientes',
      subtitle:
        "Découvrez les retours des familles que j'ai eu l'honneur d'accompagner.",
      list: [
        {
          quote:
            "Aissa a été un véritable rayon de lumière pendant ma grossesse. Son approche tenant compte des traumatismes m'a permis de me sentir en sécurité et écoutée pour la première fois.",
          name: 'S&C',
          context: 'Préparation à la naissance et soins postnataux',
          date: 'Octobre 2023',
        },
        {
          quote:
            "Le soutien en lactation que j'ai reçu a changé ma vie. Aissa a été si patiente et douce, m'aidant à naviguer dans un parcours d'allaitement très difficile avec confiance.",
          name: 'Emily R.',
          context: 'Consultation en lactation',
          date: 'Janvier 2024',
        },
        {
          quote:
            "Avoir des consultations en français tout en vivant en Nouvelle-Zélande a été incroyablement réconfortant. L'expertise et la chaleur d'Aissa ont rendu notre transition vers la parentalité beaucoup plus douce.",
          name: 'Camille T.',
          context: 'Soutien de grossesse en ligne',
          date: 'Novembre 2023',
        },
        {
          quote:
            "La profonde compréhension d'Aissa des sensibilités culturelles et sa nature compatissante la distinguent vraiment. Elle m'a donné les moyens de me défendre pendant l'accouchement.",
          name: 'Amina K.',
          context: 'Préparation à la naissance autonome',
          date: 'Mars 2024',
        },
        {
          quote:
            "J'ai contacté Aissa pour un soutien postnatal alors que je me sentais complètement dépassée. Ses conseils étaient pratiques, rassurants et exactement ce dont j'avais besoin.",
          name: 'Jessica L.',
          context: 'Soutien postnatal',
          date: 'Février 2024',
        },
      ],
    },
    recommendations: {
      title: 'Professionnels recommandés',
      subtitle:
        'Des collègues et spécialistes de confiance qui complètent les soins de sage-femme.',
      list: [
        {
          name: 'Juliette Cazabat',
          specialty: 'Psychothérapeute & Conseillère relationnelle',
          description:
            'Spécialisée dans la santé mentale maternelle, la dépression post-partum, la récupération après un traumatisme de naissance et le soutien familial. Elle travaille dans une perspective holistique.',
          contact: 'juliette-cazabat.com',
        },
        {
          name: 'Alex',
          specialty: 'Photographie de maternité',
          description:
            'Capturer le beau voyage de la maternité et offrir des souvenirs précieux avec des séances photo professionnelles.',
          contact: 'portfolio',
          buttonLabel: 'Portfolio',
        },
        {
          name: 'Forever Living Products',
          specialty: 'Produits de bien-être',
          description:
            "Une gamme de produits naturels à base d'Aloe vera pur, soutenant la santé, le bien-être et les soins personnels pendant le parcours de maternité.",
          contact: 'foreverliving.com/gbr/en-gb/home',
          buttonLabel: 'Acheter',
        },
      ],
    },
  },
}
